import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Flight as FlightIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface PlaneLoaderProps {
  /** Testo sotto l'aereo. Omesso, mostra solo l'animazione. */
  message?: string;
  /** Diametro dei cerchi. 88 e' la misura della sovrapposizione a schermo intero. */
  size?: number;
  /** Colore del testo e dell'aereo. Su fondo scuro serve il bianco. */
  light?: boolean;
  /** Occupa verticalmente lo spazio disponibile, per l'attesa di una pagina intera. */
  fullHeight?: boolean;
}

/**
 * L'aereo che gira, usato ovunque si aspetti qualcosa.
 *
 * Stava dentro LoaderContext e si poteva vedere solo come sovrapposizione a
 * schermo intero: le pagine che aspettano i propri dati mostravano un cerchio
 * qualunque, o una riga di testo, e il software sembrava fatto da mani diverse.
 * Qui la stessa animazione diventa riusabile anche in mezzo a una pagina.
 */
const PlaneLoader: React.FC<PlaneLoaderProps> = ({
  message,
  size = 88,
  light = false,
  fullHeight = false,
}) => {
  const theme = useTheme();
  const planeColor = light ? '#fff' : theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...(fullHeight ? { minHeight: '50vh' } : { py: 4 }),
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          size={size}
          thickness={1.5}
          sx={{ color: theme.palette.primary.main, opacity: 0.3 }}
        />
        <CircularProgress
          size={size}
          thickness={2.5}
          sx={{
            color: theme.palette.primary.main,
            position: 'absolute',
            top: 0,
            left: 0,
            animationDuration: '1.2s',
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FlightIcon
            sx={{
              fontSize: size * 0.41,
              color: planeColor,
              transform: 'rotate(-45deg)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              animation: 'planeFloat 2s ease-in-out infinite',
              '@keyframes planeFloat': {
                '0%, 100%': { transform: 'rotate(-45deg) translateY(0)' },
                '50%': { transform: 'rotate(-45deg) translateY(-3px)' },
              },
            }}
          />
        </Box>
      </Box>

      {message && (
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            fontWeight: 500,
            letterSpacing: '0.05em',
            fontSize: '1rem',
            color: light ? 'rgba(255,255,255,0.9)' : 'text.secondary',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default PlaneLoader;
